import React, {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import MoleculeModal from '@s-ui/react-molecule-modal'
import {listRelatedAdsContactTypes} from '@adv-mt/mt-list-related-ads'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'

import {DEFAULT_MODAL_TITLE} from './config.js'
import ModalContent, {modalRelatedAdsLeadTargets} from './modal-content.js'
import {getContactedAd} from './utils.js'

const ModalRelatedAds = ({
  ads = [],
  contactType = listRelatedAdsContactTypes.email,
  hasClickableCards = false,
  hasCriteoEvents = false,
  leadTarget = modalRelatedAdsLeadTargets.relatedVehicle,
  message,
  modalDescription = '',
  modalTitle = DEFAULT_MODAL_TITLE,
  onClickRelatedAd = () => {},
  onContactAd = () => {},
  onModalClose,
  onToggleModal = () => {},
  showInitialNotification = true,
  showModal = false,
  userData
}) => {
  const {i18n, domain, logger} = useContext(Context)
  const [modalAds, setModalAds] = useState(ads)
  const show = showModal && modalAds.length > 0
  const hasContactedAds = modalAds.some(getContactedAd)
  const hasNotContactedAds = hasContactedAds === false
  const shouldTrack = show && hasNotContactedAds

  useEffect(() => {
    const relatedAds = modalAds.map(ad => ad.id)
    shouldTrack && onToggleModal({isOpen: true, relatedAds})
  }, [userData, modalAds, contactType, shouldTrack]) // eslint-disable-line

  const setContactedAd = adId => {
    const newModalAdsState = modalAds.map(ad => {
      const isAlreadyContacted = adId === ad.id ? true : ad.isAlreadyContacted
      return {...ad, isAlreadyContacted}
    })

    setModalAds(newModalAdsState)
  }

  const _onRelatedAdContact = async ({leadData, ...contactData}) => {
    const {contactType, ...adData} = contactData
    const {id: adId} = adData
    if (contactType !== listRelatedAdsContactTypes.email) return setContactedAd(adId)

    const [contactByEmailError, contactByEmailResponse] = await domain
      .get('contact_by_mail_use_case')
      .execute({adId, message, buyer: userData})

    if (contactByEmailError) {
      logger?.error({
        name: `[ContactSellerRelatedAds]: "adId: ${adId}" error`,
        message: `[ContactSellerRelatedAds]: ${contactByEmailError?.message}, status: ${
          contactByEmailError?.status
        }, error: ${JSON.stringify(contactByEmailError?.error)}`
      })
    }
    if (contactByEmailResponse?.status === 'success') {
      setContactedAd(adId)
      onContactAd({id: adId, messageContactId: contactByEmailResponse?.messageContactId, leadData})
    }
  }

  const handleClickRelatedAd = ({ad, position}) => onClickRelatedAd({ad, position})

  return (
    <MoleculeModal
      isOpen={show}
      closeOnOutsideClick
      closeOnEscKeyDown
      header={i18n.t(modalTitle)}
      iconClose={<IconClose color="currentColor" size="medium" />}
      onClose={onModalClose}
    >
      <div className="mt-ModalRelatedAds">
        <ModalContent
          ads={modalAds}
          contactType={contactType}
          hasClickableCards={hasClickableCards}
          hasCriteoEvents={hasCriteoEvents}
          leadTarget={leadTarget}
          modalDescription={modalDescription}
          onRelatedAdContact={_onRelatedAdContact}
          onClickRelatedAd={handleClickRelatedAd}
          showInitialNotification={showInitialNotification}
        />
      </div>
    </MoleculeModal>
  )
}

ModalRelatedAds.displayName = 'ModalRelatedAds'

ModalRelatedAds.propTypes = {
  ads: PropTypes.array,
  contactType: PropTypes.oneOf(Object.values(listRelatedAdsContactTypes)),
  hasClickableCards: PropTypes.bool,
  hasCriteoEvents: PropTypes.bool,
  leadTarget: PropTypes.oneOf(Object.values(modalRelatedAdsLeadTargets)),
  message: PropTypes.string.isRequired,
  modalDescription: PropTypes.string,
  modalTitle: PropTypes.string,
  onClickRelatedAd: PropTypes.func,
  onContactAd: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  showInitialNotification: PropTypes.bool,
  showModal: PropTypes.bool,
  userData: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    phone: PropTypes.string
  })
}

export {modalRelatedAdsLeadTargets}

export default ModalRelatedAds
